import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { Box, Divider, Typography } from "@mui/material";

import { useServiceFeedbackInfinite } from "../../../../../api/queries/useServiceFeedback";
import { ServiceFeedbackCard, ServiceFeedbackCardLoader } from "../../../../../components/ServiceFeedbackCard";

import { Service } from "../../../../../types/service.type";
import RatingDetails from "../../../../../components/RatingDetails";

import EmptyReviewsFigure from "../../../../../assets/images/empty-reviews.png";

const REVIEW_LIMIT = 6;

const ReviewsTab = ({ programService }: { programService?: Service }) => {
  const { t } = useTranslation("goService", {
    keyPrefix: "programRequestPage",
  });
  const {
    data: reviews,
    isLoading: isUserReviewsLoading,
    isError: iseUserReviewsHasError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useServiceFeedbackInfinite({
    serviceId: programService?.id,
    limit: REVIEW_LIMIT,
  });

  const meta = reviews?.meta;
  const { ref, inView } = useInView();

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage, isFetchingNextPage]);

  const isLoading = isUserReviewsLoading || isFetchingNextPage;
  const isEmpty = reviews?.items.length === 0;

  return (
    <Box sx={serviceReviewsPageStyles.contentContainer}>
      <Box sx={serviceReviewsPageStyles.reviewsSection}>
        <Typography sx={serviceReviewsPageStyles.title}>{t("programReviews")}</Typography>
        {isEmpty && (
          <Box sx={serviceReviewsPageStyles.emptyStateContainer}>
            <Box
              component="img"
              src={EmptyReviewsFigure}
              alt="empty-reviews"
              sx={serviceReviewsPageStyles.emptyStateImage}
            />
            <Typography sx={serviceReviewsPageStyles.emptyStateText}>{t("noReviews")}</Typography>
          </Box>
        )}
        {!isEmpty && (
          <>
            <RatingDetails meta={meta} isLoading={isLoading} />
            <Typography sx={serviceReviewsPageStyles.peopleReviewsTitle}>{t("peopleReviews")}</Typography>
            <Divider />
            {reviews?.items.map((review, index) => (
              <>
                <ServiceFeedbackCard key={review.id} feedback={review} isFullHeight={false} isFullWidth={true} />
                {index !== reviews?.items.length - 1 && <Divider />}
              </>
            ))}
            {isLoading && [...Array(3)].map((_, index) => <ServiceFeedbackCardLoader key={index} />)}
            <div ref={ref} />
          </>
        )}
      </Box>
    </Box>
  );
};

export default ReviewsTab;

const serviceReviewsPageStyles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    width: "100%",
  },
  contentContainer: {
    display: "flex",
    gap: "16px",
    justifyContent: "space-between",
  },
  reviewsSection: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    width: { xs: "100%", lg: "553px" },
    borderRadius: "8px",
    border: "1px solid #EAECF0",
    background: "#FFF",
    padding: "16px",
  },
  title: {
    color: "var(--Gray-900, #1D2026)",
    fontFamily: "Montserrat",
    fontSize: "24px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "130%",
  },
  emptyStateContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
  },
  emptyStateImage: {
    width: "1467x",
    height: "140px",
  },
  emptyStateText: {
    color: "var(--Dark---600, #707070)",
    fontFamily: "Inter",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "normal",
  },
  peopleReviewsTitle: {
    color: "var(--Gray-900, #1D2026)",
    fontFamily: "Montserrat",
    fontSize: "24px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "130%",
  },
};
